import React, { useEffect, useState } from "react"
import { useParams, useHistory, useLocation } from "react-router-dom"
import { Card, CardBody, Row, Col, Input, Button } from "reactstrap"
import BootstrapTable from "react-bootstrap-table-next"
import * as moment from "moment"
import Breadcrumbs from "components/Common/Breadcrumb"
import TableLoader from "components/Common/TableLoader"
import {
  getAffiliateSummaryCall,
  getAffiliateReferredUsersCall,
} from "helpers/repository"
import { showErrorMessages } from "helpers/helpers"
import ReferredUserPerformanceModal from "./ReferredUserPerformanceModal"

const formatMoney = value =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

const EmptyState = ({ icon = "bx bx-user-x", title, subtitle }) => (
  <div className="text-center py-5">
    <i className={icon} style={{ fontSize: "48px", color: "#c3cbe4" }} />
    <h5 className="mt-3 mb-1">{title}</h5>
    {subtitle && <p className="text-muted mb-0">{subtitle}</p>}
  </div>
)

function AffiliateSummary() {
  const { id } = useParams() // Get affiliate ID from URL
  const history = useHistory()
  const location = useLocation()

  // Get affiliate data from location state as an initial fallback while the
  // authoritative summary is fetched
  const [affiliate, setAffiliate] = useState(location.state?.affiliate || null)
  const [loading, setLoading] = useState(true)

  const [referredUsers, setReferredUsers] = useState([])
  const [referredPagination, setReferredPagination] = useState(null)
  const [referredLoading, setReferredLoading] = useState(false)
  const [referredSearch, setReferredSearch] = useState("")
  const [referredStatus, setReferredStatus] = useState("all")
  const [referredPlatform, setReferredPlatform] = useState("all")
  const [referredPage, setReferredPage] = useState(1)

  const [isUserModalOpen, setIsUserModalOpen] = useState(false)
  const [selectedReferredUser, setSelectedReferredUser] = useState(null)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    const fetchSummary = async () => {
      setLoading(true)
      try {
        const response = await getAffiliateSummaryCall(id)
        if (cancelled) return
        const payload = response?.data?.data || {}
        setAffiliate({
          ...(payload.affiliate || {}),
          referral_code: payload.referral_code || payload.affiliate?.referral_code,
          affiliate_balance: payload.affiliate_balance,
          summary: payload.summary || {},
        })
      } catch (error) {
        if (cancelled) return
        showErrorMessages(error)
        setAffiliate(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchSummary()
    return () => {
      cancelled = true
    }
  }, [id])

  const fetchReferredUsers = async () => {
    if (!affiliate?.id) return
    setReferredLoading(true)
    try {
      const response = await getAffiliateReferredUsersCall(affiliate.id, {
        search: referredSearch,
        status: referredStatus,
        platform: referredPlatform === "all" ? undefined : referredPlatform,
        page: referredPage,
        per_page: 20,
      })
      const result = response?.data?.data || {}
      setReferredUsers(result.data || [])
      setReferredPagination(result.pagination || null)
    } catch (error) {
      showErrorMessages(error)
      setReferredUsers([])
    } finally {
      setReferredLoading(false)
    }
  }

  useEffect(() => {
    if (affiliate?.id) fetchReferredUsers()
  }, [affiliate?.id, referredStatus, referredPlatform, referredPage])

  useEffect(() => {
    if (!affiliate?.id) return
    const timeout = setTimeout(() => {
      setReferredPage(1)
      fetchReferredUsers()
    }, 400)
    return () => clearTimeout(timeout)
  }, [referredSearch])

  if (loading) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <TableLoader />
        </div>
      </div>
    )
  }

  if (!affiliate) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <EmptyState
            icon="bx bx-error"
            title="Affiliate not found"
            subtitle="The affiliate you're looking for doesn't exist."
          />
        </div>
      </div>
    )
  }

  const summary = affiliate?.summary || {}

  const referredColumns = [
    { dataField: "display_name", text: "Name" },
    {
      dataField: "masked_phone_number",
      text: "Phone Number",
      formatter: (_cell, row) => row.masked_phone_number || row.phone_number,
    },
    {
      dataField: "joined_at",
      text: "Joined",
      formatter: cell => (cell ? moment(cell).format("DD/MM/YYYY") : "—"),
    },
    {
      dataField: "status",
      text: "Status",
      formatter: cell => (
        <span
          className={`badge badge-soft-${
            cell?.toLowerCase() === "active" ? "success" : "secondary"
          } font-size-12`}
        >
          {cell}
        </span>
      ),
    },
    {
      dataField: "platform",
      text: "Platform",
      formatter: cell =>
        cell ? (
          <span className="badge badge-soft-info font-size-12">{cell}</span>
        ) : (
          "—"
        ),
    },
    { dataField: "bets_count", text: "Bets" },
    {
      dataField: "total_wagered",
      text: "Wagered",
      formatter: cell => formatMoney(cell),
    },
    {
      dataField: "total_earned",
      text: "Earned",
      formatter: cell => formatMoney(cell),
    },
  ]

  const cards = [
    {
      label: "Total Registrations",
      value: summary.total_signups,
      color: "#6366f1",
      bg: "#ede9fe",
    },
    {
      label: "Total Clicks",
      value: summary.referral_link_clicks,
      color: "#0891b2",
      bg: "#cffafe",
    },
    {
      label: "Verified Users (KYC)",
      value: summary.verified_users,
      color: "#16a34a",
      bg: "#dcfce7",
    },
    {
      label: "First-Time Depositors (FTDs)",
      value: summary.first_time_deposits,
      color: "#16a34a",
      bg: "#dcfce7",
    },
    {
      label: "Active Bettors",
      value: summary.active_bettors,
      color: "#0891b2",
      bg: "#cffafe",
    },
    {
      label: "Total Deposits",
      value: `₦${formatMoney(summary.total_deposits)}`,
      color: "#2563eb",
      bg: "#dbeafe",
    },
    {
      label: "Total Stakes",
      value: `₦${formatMoney(summary.wagered)}`,
      color: "#2563eb",
      bg: "#dbeafe",
    },
    {
      label: "Total Winnings",
      value: `₦${formatMoney(summary.winnings)}`,
      color: "#d97706",
      bg: "#fef3c7",
    },
    {
      label: "CPA Earnings",
      value: `₦${formatMoney(summary.total_cpa_earnings)}`,
      color: "#9333ea",
      bg: "#f3e8ff",
    },
    {
      label: "RevShare Earnings",
      value: `₦${formatMoney(summary.total_revshare_earnings)}`,
      color: "#9333ea",
      bg: "#f3e8ff",
    },
    {
      label: "Total Commission Earned",
      value: `₦${formatMoney(summary.total_earnings)}`,
      color: "#16a34a",
      bg: "#dcfce7",
    },
    {
      label: "Total Commission Paid",
      value: `₦${formatMoney(summary.total_paid)}`,
      color: "#0891b2",
      bg: "#cffafe",
    },
    {
      label: "Pending Commission",
      value: `₦${formatMoney(summary.unpaid)}`,
      color: "#dc2626",
      bg: "#fee2e2",
    },
    {
      label: "Life Earned",
      value: `₦${formatMoney(summary.life_earned)}`,
      color: "#d97706",
      bg: "#fef3c7",
    },
  ]

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Affiliate" breadcrumbItem="Affiliate Performance" />

        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="mb-1">Affiliate Performance</h4>
            <p className="text-muted mb-0">
              Breakdown for{" "}
              <span className="fw-semibold">
                {affiliate?.name || affiliate?.username} ({affiliate?.referral_code})
              </span>
            </p>
          </div>
          <Button
            color="secondary"
            onClick={() => history.push("/affiliate/dashboard")}
          >
            <i className="bx bx-arrow-back me-1" /> Back to Report
          </Button>
        </div>

        {/* Summary Cards */}
        <Row className="mb-4">
          {cards.map(({ label, value, color, bg }) => (
            <Col xl={3} lg={4} md={6} key={label} className="mb-3">
              <Card>
                <CardBody style={{ backgroundColor: bg, borderRadius: "8px" }}>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      marginBottom: "4px",
                    }}
                  >
                    {label}
                  </div>
                  <div style={{ fontSize: "18px", fontWeight: 700, color }}>
                    {value ?? 0}
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Referred Users Section */}
        <Card>
          <CardBody>
            <h5 className="mb-3">Referred Users</h5>

            <Row className="mb-3">
              <Col sm={6}>
                <Input
                  type="text"
                  placeholder="Search by username or phone number"
                  value={referredSearch}
                  onChange={e => setReferredSearch(e.target.value)}
                />
              </Col>
              <Col sm={3}>
                <Input
                  type="select"
                  value={referredStatus}
                  onChange={e => {
                    setReferredStatus(e.target.value)
                    setReferredPage(1)
                  }}
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="idle">Idle</option>
                </Input>
              </Col>
              <Col sm={3}>
                <Input
                  type="select"
                  value={referredPlatform}
                  onChange={e => {
                    setReferredPlatform(e.target.value)
                    setReferredPage(1)
                  }}
                >
                  <option value="all">All Platforms</option>
                  <option value="ANDROID">ANDROID</option>
                  <option value="IOS">IOS</option>
                  <option value="WEB">WEB</option>
                </Input>
              </Col>
            </Row>

            {referredLoading ? (
              <TableLoader />
            ) : !referredUsers || referredUsers.length === 0 ? (
              <EmptyState
                icon="bx bx-group"
                title="No referred users found"
                subtitle={
                  referredSearch || referredStatus !== "all"
                    ? "Try adjusting your search or status filter."
                    : "This affiliate hasn't referred anyone yet."
                }
              />
            ) : (
              <>
                <div className="table-responsive">
                  <BootstrapTable
                    keyField="id"
                    responsive
                    bordered={false}
                    striped={false}
                    classes="table align-middle table-nowrap table-check mytable-hover-row"
                    headerWrapperClasses="table-light"
                    columns={referredColumns}
                    data={referredUsers || []}
                    rowEvents={{
                      onClick: (e, row) => {
                        setSelectedReferredUser(row)
                        setIsUserModalOpen(true)
                      },
                    }}
                  />
                </div>
                {referredPagination && (
                  <Row className="mt-2">
                    <Col className="d-flex justify-content-end align-items-center gap-2">
                      <span className="text-muted">
                        Page {referredPagination.current_page} of{" "}
                        {referredPagination.last_page} (
                        {referredPagination.total} total)
                      </span>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        disabled={referredPage <= 1}
                        onClick={() => setReferredPage(referredPage - 1)}
                      >
                        Prev
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        disabled={referredPage >= referredPagination.last_page}
                        onClick={() => setReferredPage(referredPage + 1)}
                      >
                        Next
                      </button>
                    </Col>
                  </Row>
                )}
              </>
            )}
          </CardBody>
        </Card>
      </div>

      {isUserModalOpen && selectedReferredUser && (
        <ReferredUserPerformanceModal
          isOpen={isUserModalOpen}
          setIsOpen={setIsUserModalOpen}
          referredUser={selectedReferredUser}
        />
      )}
    </div>
  )
}

export default AffiliateSummary
